import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { selfApi } from '../../services/selfApi.js';

/**
 * Die Stempeluhr - Start, Pause, Stopp.
 *
 * Sie steht ganz oben auf "Meine Zeit" und braucht einen Klick. Das ist der
 * ganze Zweck: wer um sieben Uhr in die Küche kommt, tippt keine Uhrzeit
 * ein, und wer um vier Uhr nachts aus der Bar geht, erst recht nicht.
 *
 * Die Uhrzeiten schickt diese Seite NICHT mit. Start und Stopp stempelt der
 * Server; hier läuft nur die Anzeige. Ein Zeitpunkt aus dem Browserfenster
 * wäre die Grundlage der Lohnabrechnung, und die soll nicht davon abhängen,
 * wie die Uhr eines Telefons gestellt ist.
   */

/** Sekunden als 7:42:15 - die Sekunden, weil eine Uhr sonst tot aussieht. */
function alsDauer(sekunden) {
  const s = Math.max(0, Math.floor(sekunden));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export function Stempeluhr() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [fehler, setFehler] = useState(null);
  const [korrekturOffen, setKorrekturOffen] = useState(false);
  const [ende, setEnde] = useState('');

  const stand = useQuery({
    queryKey: ['self', 'stempel'],
    queryFn: () => selfApi.stempelStand(),
    /* Die laufende Stempelung gehört einer Person, nicht einem Fenster. Wer
       am Telefon startet und am Rechner stoppt, soll nicht erst neu laden
       müssen. Eine halbe Minute reicht - der Sekundenzeiger läuft ohnehin
       hier im Browser. */
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  /* Der Sekundenzeiger. Er zählt aus dem zuletzt geholten Stand hoch,
     statt dafür den Server zu fragen - sechzig Aufrufe pro Minute wären
     für eine Zahl, die man auch rechnen kann, eine Zumutung. */
  const [tick, setTick] = useState(0);
  const laufend = stand.data?.laufend || null;
  useEffect(() => {
    if (!laufend) {
      return undefined;
    }
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [laufend]);
  useEffect(() => {
    setTick(0);
  }, [stand.dataUpdatedAt]);

  const frisch = () => {
    queryClient.invalidateQueries({ queryKey: ['self', 'stempel'] });
    queryClient.invalidateQueries({ queryKey: ['self', 'week'] });
    queryClient.invalidateQueries({ queryKey: ['self', 'month'] });
    queryClient.invalidateQueries({ queryKey: ['self', 'timeAccount'] });
  };

  const zug = useMutation({
    mutationFn: ({ art, koerper }) => {
      if (art === 'start') return selfApi.stempelStart();
      if (art === 'pause') return selfApi.stempelPause();
      if (art === 'weiter') return selfApi.stempelWeiter();
      return selfApi.stempelStopp(koerper || {});
    },
    onSuccess: () => {
      setFehler(null);
      setKorrekturOffen(false);
      frisch();
    },
    onError: (err) => setFehler(err?.message || t('common.loadError')),
  });

  /* Ohne Stand nichts zeigen. Ein Kasten mit einem toten Knopf ist
     schlimmer als kein Kasten. */
  if (stand.isLoading || !stand.data) {
    return null;
  }
  /* Die Firma hat die Uhr abgeschaltet. Dann steht hier nichts - ein
     ausgegrauter Startknopf würde nach einem Fehler aussehen. */
  if (stand.data.modus === 'NUR_MANUELL') {
    return null;
  }

  const pausiert = Boolean(laufend?.pausiert);
  const netto = laufend ? laufend.nettoSekunden + (pausiert ? 0 : tick) : 0;
  const pause = laufend ? laufend.pauseSekunden + (pausiert ? tick : 0) : 0;

  function stoppen() {
    /* Beim vergessenen Stempel weist der Dienst den einfachen Stopp ab. Statt
       die Person in die Fehlermeldung laufen zu lassen, fragt die Maske
       gleich nach der Endzeit. */
    if (stand.data.vergessen) {
      setEnde('');
      setKorrekturOffen(true);
      return;
    }
    zug.mutate({ art: 'stopp' });
  }

  function korrekturAbsenden(e) {
    e.preventDefault();
    if (!ende) return;
    /* Aus der örtlichen Eingabe einen echten Zeitpunkt machen. Das Feld
       liefert "2026-08-28T06:30" ohne Zone; new Date() liest das als
       ORTSZEIT, und genau das ist gemeint. */
    zug.mutate({ art: 'stopp', koerper: { ende: new Date(ende).toISOString() } });
  }

  return (
    <div className="stempeluhr" data-laeuft={laufend ? (pausiert ? 'pause' : 'ja') : 'nein'}>
      <div className="stempeluhr-anzeige">
        <p className="time-account-label">{t('stempel.title')}</p>
        {laufend ? (
          <>
            <p className="stempeluhr-zeit mono">{alsDauer(netto)}</p>
            <p className="muted text-sm">
              {t('stempel.seit', { zeit: laufend.startZeit })}
              {pause > 0 ? ` · ${t('stempel.pauseGesamt', { minuten: Math.round(pause / 60) })}` : ''}
              {pausiert ? ` · ${t('stempel.pausiert')}` : ''}
            </p>
          </>
        ) : (
          <>
            <p className="stempeluhr-zeit mono stempeluhr-zeit-aus">0:00:00</p>
            <p className="muted text-sm">{t('stempel.bereit')}</p>
          </>
        )}
      </div>

      <div className="stempeluhr-knoepfe">
        {!laufend && (
          <Button variant="primary" onClick={() => zug.mutate({ art: 'start' })} disabled={zug.isPending}>
            {t('stempel.start')}
          </Button>
        )}
        {laufend && !pausiert && (
          <Button variant="default" onClick={() => zug.mutate({ art: 'pause' })} disabled={zug.isPending}>
            {t('stempel.pause')}
          </Button>
        )}
        {laufend && pausiert && (
          <Button variant="default" onClick={() => zug.mutate({ art: 'weiter' })} disabled={zug.isPending}>
            {t('stempel.weiter')}
          </Button>
        )}
        {laufend && (
          <Button variant="primary" onClick={stoppen} disabled={zug.isPending}>
            {t('stempel.stopp')}
          </Button>
        )}
      </div>

      {stand.data.vergessen && (
        <div className="alert warning stempeluhr-hinweis">
          {t('stempel.vergessen', { stunden: stand.data.maxStunden })}
        </div>
      )}
      {fehler && <div className="alert danger stempeluhr-hinweis">{fehler}</div>}

      <Modal
        open={korrekturOffen}
        onClose={() => setKorrekturOffen(false)}
        title={t('stempel.korrektur.title')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setKorrekturOffen(false)}>{t('common.cancel')}</Button>
            <Button variant="primary" onClick={korrekturAbsenden} disabled={!ende || zug.isPending}>
              {t('stempel.stopp')}
            </Button>
          </>
        }
      >
        <form onSubmit={korrekturAbsenden} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <p className="muted text-sm" style={{ margin: 0 }}>
            {t('stempel.korrektur.hinweis', {
              datum: laufend?.startDatum || '',
              zeit: laufend?.startZeit || '',
            })}
          </p>
          <label className="field">
            <span className="field-label">{t('stempel.korrektur.ende')}</span>
            <input
              className="input"
              type="datetime-local"
              value={ende}
              onChange={(e) => setEnde(e.target.value)}
              required
            />
          </label>
          {fehler && <div className="alert danger">{fehler}</div>}
        </form>
      </Modal>
    </div>
  );
}
